import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { UserStats } from '../../types';
import { Sparkles, Zap, Trophy, Star } from 'lucide-react';
interface HeaderProps {
  stats: UserStats;
  onDevModeToggle?: () => void;
}
export function Header({
  stats,
  onDevModeToggle
}: HeaderProps) {
  const progressToNextLevel = stats.xp % 250 / 250;
  const xpToNextLevel = 250 - stats.xp % 250;
  // Triple tap detection for mobile dev mode
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef<NodeJS.Timeout | null>(null);
  const handleHeaderTap = () => {
    tapCountRef.current += 1;
    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
    }
    if (tapCountRef.current === 3) {
      tapCountRef.current = 0;
      if (onDevModeToggle) {
        onDevModeToggle();
      }
    }
    tapTimerRef.current = setTimeout(() => {
      tapCountRef.current = 0;
    }, 500);
  };
  return <motion.div initial={{
    opacity: 0,
    y: -20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="mb-8" onClick={handleHeaderTap} style={{
    cursor: 'pointer',
    userSelect: 'none'
  }} data-id="element-325">
      {/* Greeting Section - Compact & Friendly */}
      <div className="flex items-center justify-between mb-4 px-1" data-id="element-326">
        <div data-id="element-327">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-2" data-id="element-328">
            Hoi {stats.name}!{' '}
            <span className="animate-wave inline-block origin-[70%_70%]" data-id="element-329">
              👋
            </span>
          </h1>
          <p className="text-slate-600 font-medium text-sm md:text-base" data-id="element-330">
            Klaar om te knallen vandaag? 🚀
          </p>
        </div>
      </div>

      {/* Integrated Stats Card - Game Style Status Bar */}
      <motion.div whileHover={{
      scale: 1.01
    }} whileTap={{
      scale: 0.99
    }} className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 bg-[length:200%_auto] animate-gradient rounded-2xl p-4 md:p-5 text-white shadow-lg shadow-indigo-200 relative overflow-hidden" data-id="element-331">
        {/* Background Pattern */}
        <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-332">
          <Trophy className="w-24 h-24 transform rotate-12 -mt-4 -mr-4" data-id="element-333" />
        </div>

        <div className="relative z-10 flex flex-col md:flex-row items-center gap-4 md:gap-8" data-id="element-334">
          {/* Level Badge */}
          <div className="flex items-center gap-3 min-w-fit" data-id="element-335">
            <div className="bg-white/20 backdrop-blur-sm p-2 rounded-xl" data-id="element-336">
              <Trophy className="w-6 h-6 text-yellow-300" data-id="element-337" />
            </div>
            <div data-id="element-338">
              <div className="text-xs font-bold text-indigo-100 uppercase tracking-wider" data-id="element-339">
                Level
              </div>
              <div className="text-3xl font-black leading-none" data-id="element-340">
                {stats.level}
              </div>
            </div>
          </div>

          {/* XP Progress Bar */}
          <div className="flex-1 w-full" data-id="element-341">
            <div className="flex justify-between text-xs font-bold text-indigo-100 mb-1.5" data-id="element-342">
              <span className="flex items-center gap-1" data-id="element-343">
                <Zap className="w-3 h-3 text-yellow-300" data-id="element-344" />
                {stats.xp} XP
              </span>
              <span data-id="element-345">
                Nog {xpToNextLevel} XP tot Level {stats.level + 1}
              </span>
            </div>

            <div className="h-4 bg-black/20 rounded-full overflow-hidden backdrop-blur-sm ring-1 ring-white/10" data-id="element-346">
              <motion.div className="h-full bg-gradient-to-r from-yellow-300 to-amber-400 shadow-[0_0_10px_rgba(252,211,77,0.5)]" initial={{
              width: 0
            }} animate={{
              width: `${progressToNextLevel * 100}%`
            }} transition={{
              duration: 1,
              ease: 'easeOut',
              delay: 0.2
            }} data-id="element-347" />
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}